import type { Components, Theme } from '@mui/material';

import { colors } from '../tokens';

export const MuiButton: Components<Theme>['MuiButton'] = {
  defaultProps: {
    disableRipple: true,
  },
  variants: [
    {
      props: { size: 'xl' },
      style: ({ theme }) => ({
        padding: theme.spacing(1.5, 2.25),
        fontSize: theme.typography.pxToRem(16),
        lineHeight: theme.typography.pxToRem(24),
      }),
    },
  ],
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.spacing(1),
      textTransform: 'none',
      fontWeight: 600,
      '& > .MuiSvgIcon-root': {
        color: 'inherit',
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: theme.spacing(0.25),
      },
      '&.Mui-disabled': {
        color: colors.black[300],
        cursor: 'not-allowed',
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
          color: colors.gray[300],
        },
      },
      '&.MuiButton-loading:not(.MuiButton-loadingPositionStart):not(.MuiButton-loadingPositionEnd)':
        {
          color: 'transparent',
        },
    }),
    sizeLarge: ({ theme }) => ({
      padding: theme.spacing(1.25, 2),
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.typography.pxToRem(24),
    }),
    sizeSmall: ({ theme }) => ({
      padding: theme.spacing(1, 1.5),
      fontSize: theme.typography.pxToRem(14),
      lineHeight: theme.typography.pxToRem(20),
    }),
    sizeMedium: ({ theme }) => ({
      padding: theme.spacing(1.25, 1.75),
      fontSize: theme.typography.pxToRem(14),
      lineHeight: theme.typography.pxToRem(20),
    }),
    contained: {
      boxShadow: 'none',
      '&:hover': { boxShadow: 'none' },
      '&:active': { boxShadow: 'none' },
      '&:focus-visible': { boxShadow: 'none' },
      '&.Mui-focusVisible': { boxShadow: 'none' },
      '&.Mui-disabled': { boxShadow: 'none' },
    },
    outlined: {
      border: 'none',
      boxShadow: `inset 0 0 0 1px ${colors.gray[200]}`,
    },
    outlinedPrimary: ({ theme }) => ({
      color: colors.black[700],
      backgroundColor: colors.common.white,
      ...theme.applyStyles('dark', {
        backgroundColor: colors.grayTrue[850],
      }),
      '&:hover': {
        backgroundColor: colors.gray[50],
        ...theme.applyStyles('dark', {
          backgroundColor: colors.grayTrue[900],
        }),
      },
      '&.Mui-disabled': {
        boxShadow: 'none',
      },
      ...theme.applyStyles('dark', {
        color: colors.black[100],
        boxShadow: `inset 0 0 0 1px ${colors.black[600]}`,
      }),
    }),
    textPrimary: ({ theme }) => ({
      color: colors.black[700],
      '&:hover': {
        backgroundColor: colors.gray[50],
        ...theme.applyStyles('dark', {
          backgroundColor: colors.grayTrue[850],
        }),
      },
      ...theme.applyStyles('dark', {
        color: colors.black[100],
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
          color: colors.black[200],
        },
      }),
    }),
    containedPrimary: ({ theme }) => ({
      backgroundColor: colors.black[950],
      color: colors.common.white,
      ...theme.applyStyles('dark', {
        backgroundColor: colors.green[500],
        color: colors.black[950],
      }),
      '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root': {
        color: 'inherit',
      },
      '&:hover': {
        backgroundColor: colors.black[900],
        ...theme.applyStyles('dark', {
          backgroundColor: colors.green[600],
        }),
      },
      '&.Mui-disabled': {
        backgroundColor: colors.gray[50],
        boxShadow: `inset 0 0 0 1px ${colors.gray[200]}`,
        ...theme.applyStyles('dark', {
          color: colors.black[950],
          backgroundColor: colors.green[700],
          boxShadow: 'none',
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            color: colors.grayTrue[900],
          },
        }),
      },
    }),
  },
};
